import { CalendarClock, Smartphone, TriangleAlert } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/shared/ui/Button';
import type { LicenseNotice } from './licenseGate';

/**
 * Giriş tamamlanmadan önce gösterilen abonelik/lisans bilgisi.
 *
 * Engellemez, bilgilendirir: sunucu girişi kabul etti, kullanıcı "Devam et" diyerek
 * içeri girer. Amaç, süresi bitmiş bir abonelikte hatayı ekranın ortasında değil
 * girişte görmesi ve yöneticisine gitmesi.
 */
export function LicenseNoticePanel({
  notice,
  onContinue,
}: {
  notice: LicenseNotice;
  onContinue: () => void;
}) {
  const { t, i18n } = useTranslation();
  const date = notice.date ? notice.date.toLocaleDateString(i18n.language) : '';

  const tone =
    notice.kind === 'expired'
      ? { ring: 'border-danger/40 bg-danger/10', fg: 'text-danger', Icon: TriangleAlert }
      : notice.kind === 'expiring'
        ? { ring: 'border-warning/40 bg-warning/10', fg: 'text-warning', Icon: CalendarClock }
        : { ring: 'border-brand-500/40 bg-brand-500/10', fg: 'text-brand-600', Icon: Smartphone };

  const body =
    notice.kind === 'expired'
      ? t('auth.license.expiredBody', { date })
      : notice.kind === 'expiring'
        ? t('auth.license.expiringBody', { date, days: notice.days })
        : t('auth.license.nodeOnlyBody');

  return (
    <div className="octo-rise flex flex-col gap-4" role="alert">
      <div className={`flex items-start gap-3 rounded-lg border p-3 ${tone.ring}`}>
        <tone.Icon className={`mt-0.5 h-5 w-5 shrink-0 ${tone.fg}`} />
        <div className="min-w-0">
          <p className="text-sm font-semibold">{t(`auth.license.${notice.kind}Title`)}</p>
          <p className="text-fg-muted mt-0.5 text-sm">{body}</p>
        </div>
      </div>
      <Button size="lg" className="w-full" onClick={onContinue} autoFocus>
        {t('auth.license.continue')}
      </Button>
    </div>
  );
}
